/** Ответы сотрудников на вопрос: автор, дата, статус (ждёт карточки / в карточке N), «полный», текст,
 * записи голоса с расшифровкой под плеером, история правок текста. Сотруднику API отдаёт только его
 * ответ, редактору — все; редактор отмечает «годится для карточки» (`canSelect`). */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Trash2 } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { ContentDiff } from "@/components/knowledge/ContentDiff";
import { type AnswerVoice, answerVoiceUrl, loadAnswerRevisions, type QuestionAnswer, selectAnswer } from "@/lib/knowledge";
import { errText, when } from "@/pages/KnowledgeCardPage";

const длина = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

/** Запись голоса: плеер, под ним — расшифровка, пока её нет — «расшифровывается». */
export function VoiceNote({ v, onDelete }: { v: AnswerVoice; onDelete?: (() => void) | undefined }) {
  return (
    <div className="rounded-[var(--radius-ui)] border border-border bg-bg-subtle px-2 py-1.5">
      <div className="flex items-center gap-2">
        <audio controls preload="none" src={answerVoiceUrl(v.id)} className="h-8 min-w-0 flex-1" />
        <span className="shrink-0 text-[11px] tabular-nums text-fg-faint">{длина(v.seconds)}</span>
        {onDelete && (
          <button
            type="button"
            className="shrink-0 rounded p-1 text-fg-faint hover:bg-bg-hover hover:text-err"
            title="Удалить запись"
            onClick={() => window.confirm("Удалить эту запись голоса?") && onDelete()}
          >
            <Trash2 size={13} />
          </button>
        )}
      </div>
      <div className="mt-1 text-[12.5px]">
        {v.status === "done" && <span className="whitespace-pre-wrap text-fg">{v.text}</span>}
        {(v.status === "wait" || v.status === "work") && (
          <span className="text-fg-faint">
            <span className="mr-1.5 inline-block size-1.5 animate-pulse rounded-full bg-accent align-middle" />
            Расшифровывается — запись уже сохранена, ждать не нужно
          </span>
        )}
        {v.status === "failed" && (
          <span className="text-warn" title={v.error}>
            Не расшифровалось — запись сохранена, её прослушают
          </span>
        )}
      </div>
    </div>
  );
}

/** Редакции текста ответа, новые сверху; у каждой — что поменялось против предыдущей. */
export function AnswerHistory({ answerId, count }: { answerId: number; count: number }) {
  const [open, setOpen] = useState(false);
  const q = useQuery({
    queryKey: ["kb", "answer-revisions", answerId, count],
    queryFn: () => loadAnswerRevisions(answerId),
    enabled: open,
  });
  if (count < 2) return null;
  return (
    <div className="text-[12px]">
      <button type="button" className="text-fg-muted underline hover:text-fg" onClick={() => setOpen(!open)}>
        {open ? "Скрыть историю правок" : `История правок · ${count}`}
      </button>
      {open && (
        <div className="mt-1.5 space-y-2">
          {q.error && <div className="text-err">{errText(q.error)}</div>}
          {!q.data && !q.error && <div className="text-fg-faint">Загрузка…</div>}
          {q.data &&
            [...q.data].reverse().map((r, i, all) => (
              <div key={r.id} className="border-l-2 border-border pl-2">
                <div className="text-[11px] text-fg-faint">
                  {when(r.date)}
                  {i === 0 && " · сейчас"}
                </div>
                <ContentDiff before={all[i + 1]?.text ?? ""} after={r.text} />
              </div>
            ))}
        </div>
      )}
    </div>
  );
}

/** «Годится для карточки» — отбор редактора; ответ после отбора правили — предупреждаем. */
function Pick({ a }: { a: QuestionAnswer }) {
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const toggle = async () => {
    setBusy(true);
    setError("");
    try {
      const x = await selectAnswer(a.id, !a.selected);
      qc.setQueryData<QuestionAnswer[]>(["kb", "answers", a.question_id], (old) => (old ?? []).map((y) => (y.id === x.id ? x : y)));
      void qc.invalidateQueries({ queryKey: ["kb", "gaps"] });
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="flex flex-wrap items-center gap-2 text-[12px]">
      <label className="flex cursor-pointer items-center gap-1.5 text-fg">
        <input type="checkbox" checked={a.selected} disabled={busy} onChange={() => void toggle()} />
        Годится для карточки
      </label>
      {a.changed_after_select && <span className="text-warn">изменён после отбора — перечитайте</span>}
      {error && <span className="text-err">{error} — отметка не сохранилась</span>}
    </div>
  );
}

export function AnswerList({
  answers,
  meId,
  canSelect,
}: {
  answers: QuestionAnswer[];
  meId?: number | undefined;
  /** Редактор: отбор «годится для карточки» у ответов, ещё не вошедших в карточку. */
  canSelect?: boolean;
}) {
  if (!answers.length) return null;
  return (
    <div className="mt-2 space-y-2">
      {answers.map((a) => (
        <div
          key={a.id}
          className={`space-y-1 border-l-2 pl-3 text-[12.5px] ${a.selected && a.status === "new" ? "border-accent" : "border-border"}`}
        >
          <div className="flex flex-wrap gap-x-2 text-[11.5px] text-fg-faint">
            <span className="text-fg-muted">{a.user_id === meId ? "Мой ответ" : a.author || "без имени"}</span>
            <span>{when(a.date)}</span>
            {a.voices.length > 0 && <span>голосом · {a.voices.length}</span>}
            {a.done ? <span className="text-ok">ответ полный</span> : <span>не отмечен полным</span>}
            {a.status === "used" && a.card_id ? (
              <Link to={`/knowledge/${a.card_id}`} className="text-ok underline">
                вошёл в карточку {a.card_id}
              </Link>
            ) : a.selected ? (
              <span className="text-accent">отобран для карточки</span>
            ) : (
              <span className="text-warn">ждёт сборки карточки</span>
            )}
          </div>
          {a.text && <div className="whitespace-pre-wrap text-fg">{a.text}</div>}
          {a.voices.map((v) => (
            <VoiceNote key={v.id} v={v} />
          ))}
          <AnswerHistory answerId={a.id} count={a.revisions} />
          {canSelect && a.status === "new" && (a.text || a.voices.length > 0) && <Pick a={a} />}
        </div>
      ))}
    </div>
  );
}
